import { MastersCard } from "@components/AdministratorPanel/components/MastersCard";
import { SourcesCard } from "@components/AdministratorPanel/components/SourcesCard";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  LinearProgress,
  MenuItem,
  Modal,
  Select,
  SelectChangeEvent,
  TextField,
  Typography
} from "@mui/material";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { Theme, useTheme } from "@mui/material/styles";
import { useKeycloak } from "@react-keycloak/web";
import { accessRolesService } from "@services/accessManagerApi/accesRolesService";
import {
  AssignRoleToUserRequest,
  CreateRoleRequest,
  DeleteRoleRequest,
  UnassignRoleToUserRequest
} from "@services/accessManagerApi/apiRequestsModels";
import { Roles } from "@services/accessManagerApi/models/Roles";
import { sourceManagerApiService } from "@services/sourceManagerApi/SourceManagerApiService";
import {
  UpdateSourceCategoriesRequest,
  UploadSourcesRequest
} from "@services/sourceManagerApi/apiRequestsModels";
import { Source } from "@services/sourceManagerApi/models/Source";
import { SourceStatus } from "@services/sourceManagerApi/models/SourceStatus";
import { t } from "i18next";
import React, { useEffect, useRef, useState } from "react";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250
    }
  }
};

const AdministratorPanel = () => {
  const [roles, setRoles] = useState<Roles[]>([]);
  const [isLoading] = useState(false);
  const { keycloak } = useKeycloak();

  const [selectedRoleId, setSelectedRoleId] = useState<number>(0);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number>(0);
  const [loadingRoles, setLoadingRoles] = useState(true);

  // Uncompleted Source IDS logic
  const [uncompletedSourceIds, setUncompletedSourceIds] = useState<number[]>(
    []
  );
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const [sources, setSources] = useState<Source[]>([]);
  const [loadingSources, setIsLoadingSources] = useState(false);

  // Upload sources modal
  const [uploadSourcesModalOpen, setUploadSourcesModalOpen] = useState(false);
  const [roleSelectedUpload, setRoleSelectedUpload] = useState<number[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Duplicated files state
  const [duplicatedFiles, setDuplicatedFiles] = useState<string[]>([]);

  // DeleteSource confirmation modal
  const [deleteSourceConfirmOpen, setDeleteSourceConfirmOpen] = useState(false);
  const [sourceIdToDelete, setSourceIdToDelete] = useState<number | null>(null);

  // Update roles modal
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [rolesForSource, setRolesForSource] = useState<number[]>([]);
  const [currentSourceId, setCurrentSourceId] = useState<number | null>(null);

  // Tabs
  const [tabValue, setTabValue] = React.useState(0);

  // Create Role Modal
  const [createRolesModalOpen, setCreateRolesModalOpen] = useState(false);
  const [roleCreationName, setRoleCreationName] = useState<string | null>("");
  const [roleCreationDescription, setRoleCreationDescription] = useState<
    string | null
  >("");
  const [roleCreationError, setRoleCreationError] = useState<string | null>(
    null
  );
  const [isCreatingRole, setIsCreatingRole] = useState(false);

  // Delete Role confirmation modal
  const [deleteRoleConfirmOpen, setDeleteRoleConfirmOpen] = useState(false);
  const [roleIdToDelete, setRoleIdToDelete] = useState<number | null>(null);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const theme = useTheme<Theme>();

  const loadRoles = async () => {
    try {
      setLoadingRoles(true);
      const response = await accessRolesService.getAllRoles();
      const list = ([] as Roles[]).concat(response);
      setRoles(list);

      if (list.length > 0) {
        const firstRole = list[0];
        setSelectedRoleId(firstRole.id);

        // 2) Inicializamos también la categoría
        const catId = firstRole.categories[0]?.id ?? 0;
        setSelectedCategoryId(catId);
      }

      setLoadingRoles(false);
    } catch (e) {
      console.error("Error al cargar roles:", e);
    }
  };

  const loadSourcesByCategory = async (categoryId: number) => {
    if (categoryId === 0) {
      setSources([]);
      return;
    }

    try {
      setIsLoadingSources(true);
      const sourcesData =
        await sourceManagerApiService.getSourcesFilteredByCategoryId([
          categoryId
        ]);
      setSources(sourcesData);
    } catch (error) {
      console.error("Error al cargar las fuentes filtradas:", error);
    } finally {
      setIsLoadingSources(false);
    }
  };

  // Función para ordenar las fuentes: primero las que están cargando, luego las completadas
  const getSortedSources = (sources: Source[]): Source[] => {
    return [...sources].sort((a, b) => {
      const aIsLoading =
        a.status === SourceStatus.PROCESSING ||
        a.status === SourceStatus.SUBMITTED;
      const bIsLoading =
        b.status === SourceStatus.PROCESSING ||
        b.status === SourceStatus.SUBMITTED;

      // Si 'a' está cargando y 'b' no, 'a' va primero (return -1)
      if (aIsLoading && !bIsLoading) return -1;
      // Si 'b' está cargando y 'a' no, 'b' va primero (return 1)
      if (!aIsLoading && bIsLoading) return 1;
      // Si ambos tienen el mismo estado, mantener el orden original
      return 0;
    });
  };

  useEffect(() => {
    loadRoles();
  }, []);

  // Charge sources when selectedCategoryId changes
  useEffect(() => {
    if (selectedCategoryId !== 0) {
      loadSourcesByCategory(selectedCategoryId);
    }
  }, [selectedCategoryId]);

  useEffect(() => {
    // @ts-ignore
    if (roleCreationName?.length > 100) {
      setRoleCreationError(
        t(
          "administration.masters.master_must_not_have_more_than_100_characters"
        )
      );
    } else {
      setRoleCreationError(null);
    }
  }, [roleCreationName]);

  const handleRoleChange = (event: SelectChangeEvent<number>) => {
    const roleId = Number(event.target.value);
    setSelectedRoleId(roleId);

    const rol = roles.find((r) => r.id === roleId);
    const catId = rol?.categories[0]?.id ?? 0;
    setSelectedCategoryId(catId);
  };

  useEffect(() => {
    // Clear the last polling and the uncompletedSourcerIds when roleId is changed
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setUncompletedSourceIds([]);
  }, [selectedRoleId]);

  // Nuevo useEffect para detectar fuentes incompletas cuando se actualizan las fuentes
  useEffect(() => {
    // Only detect uncompleted sources if we have sources and selectedCategoryId is not 0
    if (sources.length > 0 && selectedCategoryId !== 0) {
      getUncompletedSourcesOfCategorySelected();
    } else if (sources.length === 0) {
      // If there are no sources, clear the uncompleted source IDs
      setUncompletedSourceIds([]);
    }
  }, [sources, selectedCategoryId]);

  const getUncompletedSourcesOfCategorySelected = async () => {
    try {
      // Use the sources that we already have loaded instead of making another request
      const uncompletedSources = sources.filter(
        (source) =>
          source.status === SourceStatus.PROCESSING ||
          source.status === SourceStatus.SUBMITTED
      );

      if (uncompletedSources.length > 0) {
        const uncompletedSourceIds = uncompletedSources.map(
          (source) => source.id
        );
        setUncompletedSourceIds(uncompletedSourceIds);
      } else {
        setUncompletedSourceIds([]);
      }
    } catch (error) {
      console.error("Error al obtener fuentes pendientes de subir");
    }
  };

  useEffect(() => {
    if (uncompletedSourceIds.length > 0) {
      // polling cada 10 segundos
      pollingIntervalRef.current = setInterval(async () => {
        try {
          const statuses =
            await sourceManagerApiService.getSourcesStatus(
              uncompletedSourceIds
            );

          // Separar las fuentes que aún están en procesamiento de las que ya se completaron
          const stillProcessingSources: number[] = [];
          const completedSources: number[] = [];

          statuses.forEach((status) => {
            if (
              status.status === SourceStatus.PROCESSING ||
              status.status === SourceStatus.SUBMITTED
            ) {
              stillProcessingSources.push(status.source_id);
            } else {
              completedSources.push(status.source_id);
            }
          });

          // Si hay fuentes que se completaron, actualizar la UI
          if (completedSources.length > 0) {
            await loadSourcesByCategory(selectedCategoryId);
          }

          // Actualizar la lista de fuentes incompletas solo con las que aún están en procesamiento
          if (stillProcessingSources.length === 0) {
            // Si no quedan fuentes en procesamiento, limpiar completamente el polling
            setUncompletedSourceIds([]);
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
          } else {
            // Continuar el polling solo con las fuentes que aún están en procesamiento
            setUncompletedSourceIds(stillProcessingSources);
          }
        } catch (error) {
          console.error("Error en polling de estados de fuentes:", error);
        }
      }, 10000); // 10 segundos
    } else {
      // Si no hay fuentes incompletas, limpiar el polling
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }

    // Cleanup al desmontar el componente o cambiar las dependencias
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [uncompletedSourceIds, selectedCategoryId]);

  const handleOpenUploadSourcesModal = () => setUploadSourcesModalOpen(true);
  const handleCloseUploadSourcesModal = () => {
    setUploadSourcesModalOpen(false);
    setSelectedFiles([]);
    setUploadError(null);
  };

  const handleOpenCreateRolesModal = () => setCreateRolesModalOpen(true);
  const handleCloseCreateRolesModal = () => {
    setCreateRolesModalOpen(false);
    setRoleCreationName(null);
    setRoleSelectedUpload([]);
    setUploadError(null);
  };

  const handleRoleUploadChange = (
    event: SelectChangeEvent<typeof roleSelectedUpload>
  ) => {
    const {
      target: { value }
    } = event;
    setRoleSelectedUpload(
      typeof value === "string" ? value.split(",").map((v) => Number(v)) : value
    );
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;
    setSelectedFiles((prev) => [...prev, ...Array.from(files)]);
  };

  const handleDeleteSource = async (id: number) => {
    try {
      setIsLoadingSources(true);
      const success = await sourceManagerApiService.deleteSource(id);
      if (success) {
        // Recargar solo las fuentes de la categoría actual
        await loadSourcesByCategory(selectedCategoryId);
      }
    } catch (e) {
      console.error("Error al eliminar fuente:", e);
    } finally {
      setIsLoadingSources(false);
    }
  };

  const handleDeleteRole = async (id: number) => {
    try {
      setLoadingRoles(true);
      const request: DeleteRoleRequest = {
        id: id
      };
      await accessRolesService.deleteRole(request);
      await loadRoles();
      setLoadingRoles(false);
    } catch (e) {
      console.error("Error deleting role:", e);
    }
  };

  const handleAssignRoleToUser = async (id: number) => {
    try {
      setLoadingRoles(true);
      const request: AssignRoleToUserRequest = {
        roles: [id]
      };
      await accessRolesService.assignRole(request);
      // Force refresh the token
      await keycloak.updateToken(-1);
      await loadRoles();
      setLoadingRoles(false);
    } catch (e) {
      console.error("Error assigning role:", e);
    }
  };

  const handleUnassignRoleToUser = async (id: number) => {
    try {
      setLoadingRoles(true);
      const request: UnassignRoleToUserRequest = {
        roles: [id]
      };
      await accessRolesService.unassignRole(request);
      // Force refresh the token
      await keycloak.updateToken(-1);
      await loadRoles();
      setLoadingRoles(false);
    } catch (e) {
      console.error("Error unassigning role:", e);
    }
  };

  const handleConfirmDeleteSource = async () => {
    if (sourceIdToDelete !== null) {
      await handleDeleteSource(sourceIdToDelete);
    }
    handleCloseSourceDeleteModal();
  };

  const handleConfirmDeleteRole = async () => {
    if (roleIdToDelete !== null) {
      await handleDeleteRole(roleIdToDelete);
    }
    handleCloseRoleDeleteModal();
  };

  const confirmDeleteSource = (id: number) => {
    setSourceIdToDelete(id);
    setDeleteSourceConfirmOpen(true);
  };

  const confirmDeleteRole = (id: number) => {
    setRoleIdToDelete(id);
    setDeleteRoleConfirmOpen(true);
  };

  const handleCloseSourceDeleteModal = () => {
    setDeleteSourceConfirmOpen(false);
    setSourceIdToDelete(null);
  };

  const handleCloseRoleDeleteModal = () => {
    setDeleteRoleConfirmOpen(false);
    setRoleIdToDelete(null);
  };

  const handleRemoveFile = (removeIdx: number) => {
    setSelectedFiles((prev) => prev.filter((_, idx) => idx !== removeIdx));
  };

  const handleUploadSourceSubmit = async () => {
    if (!selectedFiles || selectedFiles.length === 0) return;

    const valid_extensions = sourceManagerApiService.allowedExtensions;

    const InvalidFileFormat = selectedFiles.filter((file) => {
      const extension = file.name.split(".").pop()?.toLowerCase();
      return !extension || !valid_extensions.includes(extension);
    });

    if (InvalidFileFormat.length > 0) {
      setUploadError(t("administration.sources.check_source_format_is_valid"));
      return;
    }
    try {
      setIsUploading(true);
      const filesArray = Array.from(selectedFiles);

      const uploadRequest: UploadSourcesRequest = {
        role_ids: roleSelectedUpload,
        files: filesArray
      };

      const uploadResponse =
        await sourceManagerApiService.uploadSources(uploadRequest);
      console.log("uploadResponse", uploadResponse);

      // Separar archivos duplicados de los exitosos
      const duplicatedFileNames = uploadResponse
        .filter((item) => item.source_id === null && item.status === "FAILED")
        .map((item) => item.name);

      console.log("duplicatedFileNames", duplicatedFileNames);

      const successfulUploads = uploadResponse.filter(
        (item) => item.source_id !== null && item.status === "SUBMITTED"
      );

      console.log("successfulUploads", successfulUploads);

      // Si hay archivos duplicados, guardarlos en el state
      if (duplicatedFileNames.length > 0) {
        setDuplicatedFiles(duplicatedFileNames);
      }

      // Si hay archivos exitosos o duplicados, consideramos que la operación fue exitosa
      if (uploadResponse.length > 0) {
        setIsUploading(false);
        handleCloseUploadSourcesModal();
        await loadRoles();
        // Recargar solo las fuentes de la categoría actual
        await loadSourcesByCategory(selectedCategoryId);
      }
    } catch (error) {
      console.error("Error al subir ficheros:", error);
      // @ts-ignore
      setUploadError(t("administration.files.error_processing_files"));
      setIsUploading(false);
    }
  };

  const handleCreateRoleSubmit = async () => {
    if (!roleCreationName || roleCreationName.length === 0) return;

    try {
      setIsCreatingRole(true);

      const createRoleRequest: CreateRoleRequest = {
        name: roleCreationName,
        description: roleCreationDescription ?? ""
      };

      const responseSuccess =
        await accessRolesService.createNewRole(createRoleRequest);

      if (responseSuccess) {
        setIsCreatingRole(false);
        handleCloseCreateRolesModal();
        await loadRoles();
        // Recargar solo las fuentes de la categoría actual
        await loadSourcesByCategory(selectedCategoryId);
      }
    } catch (error) {
      console.error("Error al crear rol:", error);
      // @ts-ignore
      setRoleCreationError(t("administration.masters.error_creating_master"));
      setIsCreatingRole(false);
    } finally {
      setIsCreatingRole(false);
    }
  };

  const openRoleSelectionModal = async (sourceId: number) => {
    setCurrentSourceId(sourceId);

    try {
      const fullSource = await sourceManagerApiService.getSourcesById(sourceId);
      if (fullSource) {
        const preselectedRoleIds = fullSource.categories
          .map((cat) => {
            // buscamos el rol que tenga esta categoría
            const rol = roles.find((r) =>
              r.categories.some((c) => c.id === cat.id)
            );
            return rol ? rol.id : null;
          })
          .filter((id): id is number => id !== null);
        setRolesForSource(preselectedRoleIds);
      }
    } catch (error) {
      console.error("Error al obtener la fuente completa:", error);
    }

    setRoleModalOpen(true);
  };

  const handleRoleModalClose = () => {
    setRoleModalOpen(false);
    setRolesForSource([]);
    setCurrentSourceId(null);
  };

  const handleRolesForSourceChange = (
    event: SelectChangeEvent<typeof rolesForSource>
  ) => {
    const { value } = event.target;
    setRolesForSource(
      typeof value === "string" ? value.split(",").map((v) => +v) : value
    );
  };

  const handleUpdateRolesForSource = async () => {
    if (currentSourceId === null) return;
    const category_ids = rolesForSource.map((roleId) => {
      const rol = roles.find((r) => r.id === roleId);
      return rol!.categories[0].id;
    });

    try {
      const updateRequest: UpdateSourceCategoriesRequest = {
        categories_ids: category_ids,
        source_id: currentSourceId
      };
      await sourceManagerApiService.updateSourceCategories(updateRequest);
      handleRoleModalClose();
      // Recargar solo las fuentes de la categoría actual
      await loadSourcesByCategory(selectedCategoryId);
    } catch (e) {
      console.error("Error al guardar roles del source:", e);
    }
  };

  interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
  }

  function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
      </div>
    );
  }

  return (
    <Box
      className="w-full"
      sx={{
        display: "flex",
        flexDirection: "column"
      }}
    >
      <Box>
        <Grid container sx={{ flexGrow: 1, height: "100%" }}>
          {isLoading && (
            <Box className="w-full">
              <LinearProgress color="primary" />
            </Box>
          )}
          <Box
            width="100%"
            paddingX={2}
            paddingY={1}
            sx={{
              display: "flex",
              flexDirection: "column",
              flexGrow: 1
            }}
          >
            <Box
              sx={{
                padding: 2,
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                backgroundColor: "white",
                borderRadius: 2,
                boxShadow: "0px 4px 16px rgba(0,0,0,0.05)",
                height: "100%",
                overflow: "hidden"
              }}
            >
              <Box>
                {/* Fonts Tab */}
                <Tabs value={tabValue} onChange={handleChange}>
                  <Tab
                    label={t("administration.sources.sources")}
                    sx={{
                      fontWeight: 600,
                      textTransform: "capitalize",
                      fontSize: { xs: 15.5, sm: 19 }
                    }}
                  />
                  <Tab
                    label={t("administration.masters.masters")}
                    sx={{
                      fontWeight: 600,
                      textTransform: "capitalize",
                      fontSize: { xs: 15.5, sm: 19 }
                    }}
                  />
                </Tabs>
                <CustomTabPanel value={tabValue} index={0}>
                  <Box sx={{ display: "flex", flexDirection: "row", mb: 1 }}>
                    <Typography
                      component="h2"
                      sx={{
                        fontSize: {
                          xs: 16,
                          sm: 20, // desde 600px +
                          md: 23, // desde 900px +
                          lg: 25 // desde 1200px +}
                        },
                        fontWeight: 700
                      }}
                    >
                      {t("administration.sources.sources_selection")}
                    </Typography>
                    <Button
                      variant="contained"
                      sx={{
                        marginLeft: 3,
                        fontSize: { xs: 13, sm: 16 },
                        backgroundColor: "primary.main",
                        "&:hover": {
                          backgroundColor: "customColors.core"
                        }
                      }}
                      onClick={handleOpenUploadSourcesModal}
                    >
                      {t("common.add")}
                    </Button>
                  </Box>
                  {duplicatedFiles.length > 0 && (
                    <Box sx={{ display: "flex", flexDirection: "row", mb: 1 }}>
                      <Alert
                        severity="warning"
                        onClose={() => setDuplicatedFiles([])}
                      >
                        Los documentos ya existen:{" "}
                        {duplicatedFiles.map((fileName, index) => (
                          <span key={index}>
                            <strong>{fileName}</strong>
                            {index < duplicatedFiles.length - 1 ? ", " : ""}
                          </span>
                        ))}
                      </Alert>
                    </Box>
                  )}
                  <Grid container mb={4}>
                    <Grid item xs={12} sm={5} md={4} lg={3}>
                      <Typography
                        component="h2"
                        sx={{
                          fontSize: {
                            xs: 14,
                            sm: 16, // desde 600px +
                            md: 18, // desde 900px +
                            lg: 19 // desde 1200px +}
                          },
                          fontWeight: 550,
                          marginTop: 1.3,
                          marginBottom: 1
                        }}
                      >
                        {t("administration.masters.filter_by_master")}
                      </Typography>
                      {loadingRoles ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            height: 40
                          }}
                        >
                          <CircularProgress size={24} sx={{ mr: 2 }} />
                          <Typography>
                            {t("administration.masters.loading_masters")}
                          </Typography>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            height: 40,
                            gap: 2
                          }}
                        >
                          <Select
                            fullWidth
                            variant="standard"
                            disableUnderline
                            value={selectedRoleId}
                            onChange={handleRoleChange}
                            displayEmpty
                            sx={{
                              height: 40,
                              border: "1px solid #D1D5DB",
                              borderRadius: 2.5,
                              px: 2,
                              fontSize: 12.5,
                              "& .MuiSelect-select:focus": {
                                backgroundColor: "transparent"
                              },
                              ".MuiSelect-icon": { right: 12 }
                            }}
                          >
                            <MenuItem value={0} disabled>
                              {t("chatbot.settings.select_one", "Elige uno")}
                            </MenuItem>
                            {roles.map((role) => (
                              <MenuItem key={role.id} value={role.id}>
                                <Typography sx={{ fontSize: 15 }}>
                                  {role.name}
                                </Typography>
                              </MenuItem>
                            ))}
                          </Select>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    spacing={3}
                    direction="row"
                    sx={{ flexGrow: 1 }}
                  >
                    <SourcesCard
                      loading={loadingSources}
                      sources={getSortedSources(sources)}
                      isSourceActive={(_) => true}
                      onDelete={confirmDeleteSource}
                      onOpenRoleModal={openRoleSelectionModal}
                    />
                  </Grid>
                </CustomTabPanel>

                {/* Roles Tab */}
                <CustomTabPanel value={tabValue} index={1}>
                  <Box sx={{ display: "flex", flexDirection: "row", mb: 3 }}>
                    <Typography
                      component="h2"
                      sx={{
                        fontSize: {
                          xs: 16,
                          sm: 20, // desde 600px +
                          md: 23, // desde 900px +
                          lg: 25 // desde 1200px +}
                        },
                        fontWeight: 700
                      }}
                    >
                      {t("administration.masters.master_management")}
                    </Typography>
                    <Button
                      variant="contained"
                      sx={{
                        marginLeft: 3,
                        fontSize: { xs: 13, sm: 16 },
                        backgroundColor: "primary.main",
                        "&:hover": {
                          backgroundColor: "customColors.core"
                        }
                      }}
                      onClick={handleOpenCreateRolesModal}
                    >
                      {t("common.add")}
                    </Button>
                  </Box>

                  {loadingRoles ? (
                    <Box
                      sx={{ display: "flex", alignItems: "center", height: 40 }}
                    >
                      <CircularProgress size={24} sx={{ mr: 2 }} />
                      <Typography>
                        {t("administration.masters.loading_masters")}
                      </Typography>
                    </Box>
                  ) : (
                    <Grid
                      container
                      spacing={3}
                      direction="row"
                      sx={{ flexGrow: 1 }}
                    >
                      <MastersCard
                        loading={loadingRoles}
                        roles={roles}
                        onDelete={confirmDeleteRole}
                        onOpenRoleModal={openRoleSelectionModal}
                        onAssignRoleToUser={handleAssignRoleToUser}
                        onUnassignRoleToUser={handleUnassignRoleToUser}
                      />
                    </Grid>
                  )}
                </CustomTabPanel>

                {/* Sources TAB */}
                {/* Create Source Modal*/}
                <Modal
                  open={uploadSourcesModalOpen}
                  onClose={handleCloseUploadSourcesModal}
                  aria-labelledby="upload-sources"
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: 450,
                      backgroundColor: "background.paper",
                      borderRadius: 2,
                      boxShadow: 24,
                      p: 4
                    }}
                  >
                    <FormControl sx={{ mt: 2, mb: 3, width: "100%" }}>
                      <Typography
                        variant="h6"
                        component="h2"
                        fontSize={17}
                        fontWeight={550}
                        mb={0.6}
                      >
                        {t("administration.masters.select_masters")}
                      </Typography>
                      <Select
                        fullWidth
                        multiple
                        displayEmpty
                        variant="standard"
                        disableUnderline
                        value={roleSelectedUpload}
                        onChange={handleRoleUploadChange}
                        renderValue={(selected) => (
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                          >
                            {selected.map((roleId) => {
                              const role = roles.find((r) => r.id === roleId);
                              return <Chip key={roleId} label={role?.name} />;
                            })}
                          </Box>
                        )}
                        MenuProps={MenuProps}
                        disabled={isUploading}
                        sx={{
                          height: "auto",
                          border: "1px solid #b9bbbf",
                          borderRadius: 2.5,
                          px: 1,
                          py: 0.5,
                          fontSize: 12.5,
                          "& .MuiSelect-select:focus": {
                            backgroundColor: "transparent"
                          },
                          ".MuiSelect-icon": { right: 12 }
                        }}
                      >
                        <MenuItem value="" disabled>
                          {t("chatbot.settings.select_one", "Elige uno")}
                        </MenuItem>
                        {roles.map((role) => (
                          <MenuItem
                            key={role.id}
                            value={role.id}
                            style={{
                              fontWeight: roleSelectedUpload.includes(role.id)
                                ? theme.typography.fontWeightMedium
                                : theme.typography.fontWeightRegular
                            }}
                          >
                            {role.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Box mb={4} mt={2}>
                      <Typography
                        variant="h6"
                        component="h2"
                        fontSize={17}
                        fontWeight={550}
                        mb={0.6}
                      >
                        {t("administration.sources.select_the_sources")}
                      </Typography>
                      {selectedFiles && (
                        <Box sx={{ mb: 2, overflowY: "auto", maxHeight: 400 }}>
                          {Array.from(selectedFiles).map((file, idx) => (
                            <Box
                              key={idx}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 0.5
                              }}
                            >
                              <Typography
                                key={idx}
                                variant="body2"
                                sx={{
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  width: "250px"
                                }}
                              >
                                {file.name}
                              </Typography>
                              <DeleteIcon
                                sx={{
                                  color: "red",
                                  fontSize: 20,
                                  cursor: "pointer",
                                  ml: 1
                                }}
                                onClick={() => handleRemoveFile(idx)}
                              />
                            </Box>
                          ))}
                        </Box>
                      )}

                      <Box
                        display="flex"
                        flexDirection="row"
                        justifyContent="start"
                        alignItems="center"
                        mb={1}
                      >
                        <Button
                          variant="contained"
                          component="label"
                          disabled={isUploading}
                          sx={{
                            mb: 2,
                            backgroundColor: "primary.main",
                            "&:hover": {
                              backgroundColor: "customColors.core"
                            }
                          }}
                        >
                          {selectedFiles.length > 0
                            ? t("administration.files.upload_other_files")
                            : t("administration.files.upload_files")}
                          <input
                            type="file"
                            multiple
                            hidden
                            onChange={handleFileChange}
                          />
                        </Button>
                      </Box>
                    </Box>

                    {uploadError && (
                      <Typography
                        variant="body2"
                        color="error"
                        sx={{ mt: 1, mb: 2 }}
                      >
                        {uploadError}
                      </Typography>
                    )}

                    <Button
                      variant="contained"
                      fullWidth
                      disabled={
                        selectedFiles.length === 0 ||
                        roleSelectedUpload.length === 0 ||
                        isUploading
                      }
                      onClick={handleUploadSourceSubmit}
                      startIcon={
                        isUploading && (
                          <CircularProgress size={20} color="inherit" />
                        )
                      }
                      sx={{
                        backgroundColor: "primary.main",
                        "&:hover": {
                          backgroundColor: "customColors.core"
                        }
                      }}
                    >
                      {isUploading
                        ? t("administration.files.uploading")
                        : t("administration.files.upload_files")}
                    </Button>
                  </Box>
                </Modal>

                {/* Update source roles modal*/}
                <Dialog
                  open={roleModalOpen}
                  onClose={handleRoleModalClose}
                  fullWidth
                  maxWidth="sm"
                >
                  <DialogContent>
                    <FormControl fullWidth variant="standard" sx={{ mt: 1 }}>
                      <Typography
                        variant="h6"
                        component="h2"
                        fontSize={15}
                        fontWeight={550}
                        mb={0.6}
                      >
                        {t("administration.masters.assigned_masters")}
                      </Typography>
                      <Select
                        multiple
                        fullWidth
                        displayEmpty
                        value={rolesForSource}
                        variant="standard"
                        disableUnderline
                        onChange={handleRolesForSourceChange}
                        sx={{
                          height: "auto",
                          border: "1px solid #b9bbbf",
                          borderRadius: 2.5,
                          px: 1,
                          py: 0.5,
                          fontSize: 12.5,
                          "& .MuiSelect-select:focus": {
                            backgroundColor: "transparent"
                          },
                          ".MuiSelect-icon": { right: 12 }
                        }}
                        renderValue={(selected) => (
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                          >
                            {selected.map((id) => {
                              const rol = roles.find((r) => r.id === id);
                              return rol ? (
                                <Chip key={id} label={rol.name} />
                              ) : null;
                            })}
                          </Box>
                        )}
                        MenuProps={MenuProps}
                      >
                        {roles.map((rol) => (
                          <MenuItem key={rol.id} value={rol.id}>
                            {rol.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </DialogContent>
                  <DialogActions>
                    <Button
                      onClick={handleRoleModalClose}
                      color="error"
                      variant="outlined"
                      sx={{
                        textTransform: "capitalize"
                      }}
                    >
                      {t("common.cancel")}
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleUpdateRolesForSource}
                      sx={{
                        backgroundColor: "primary.main",
                        "&:hover": {
                          backgroundColor: "customColors.core"
                        }
                      }}
                    >
                      {t("administration.masters.update_masters")}
                    </Button>
                  </DialogActions>
                </Dialog>

                {/* Delete Source confirmation modal*/}
                <Dialog
                  open={deleteSourceConfirmOpen}
                  onClose={handleCloseSourceDeleteModal}
                  aria-labelledby="confirm-dialog-title"
                  aria-describedby="confirm-dialog-description"
                >
                  <DialogTitle id="confirm-dialog-title">
                    {t("administration.sources.are_you_sure_to_delete_source")}
                  </DialogTitle>
                  <DialogContent>
                    <DialogContentText id="confirm-dialog-description">
                      {t("common.this_action_cannot_be_undone")}
                    </DialogContentText>
                  </DialogContent>
                  <DialogActions>
                    <Button
                      onClick={handleCloseSourceDeleteModal}
                      color="primary"
                      variant="outlined"
                      sx={{
                        textTransform: "capitalize"
                      }}
                    >
                      {t("common.cancel")}
                    </Button>
                    <Button
                      onClick={handleConfirmDeleteSource}
                      color="error"
                      variant="contained"
                      autoFocus
                      sx={{
                        textTransform: "capitalize"
                      }}
                    >
                      {t("common.delete")}
                    </Button>
                  </DialogActions>
                </Dialog>

                {/* Role TAB */}
                {/* Create Role Modal*/}
                <Modal
                  open={createRolesModalOpen}
                  onClose={handleCloseCreateRolesModal}
                  aria-labelledby="create-roles"
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: 450,
                      backgroundColor: "background.paper",
                      borderRadius: 2,
                      boxShadow: 24,
                      p: 4
                    }}
                  >
                    <FormControl sx={{ mt: 2, mb: 3, width: "100%" }}>
                      <Typography
                        variant="h6"
                        component="h2"
                        fontSize={17}
                        fontWeight={550}
                        mb={0.6}
                      >
                        {t("administration.masters.master_name")}
                      </Typography>
                      <TextField
                        required
                        placeholder="Administrator"
                        value={roleCreationName ?? ""}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          setRoleCreationName(event.target.value);
                        }}
                        error={Boolean(roleCreationError)}
                        type="text"
                        fullWidth
                        multiline
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "&.Mui-focused fieldset": {
                              borderColor: "gray",
                              borderWidth: "1px"
                            }
                          },
                          marginBottom: 2
                        }}
                      />
                      <Typography
                        variant="h6"
                        component="h2"
                        fontSize={17}
                        fontWeight={550}
                        mb={0.6}
                        mt={2}
                      >
                        {t("administration.masters.master_description")}
                      </Typography>
                      <TextField
                        required
                        value={roleCreationDescription ?? ""}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          setRoleCreationDescription(event.target.value);
                        }}
                        error={Boolean(roleCreationError)}
                        type="text"
                        fullWidth
                        rows={2}
                        multiline
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "&.Mui-focused fieldset": {
                              borderColor: "gray",
                              borderWidth: "1px"
                            }
                          },
                          marginBottom: 2
                        }}
                      />
                    </FormControl>

                    {Boolean(roleCreationError) && (
                      <Typography
                        variant="body2"
                        color="error"
                        sx={{ mt: 1, mb: 1 }}
                      >
                        {roleCreationError}
                      </Typography>
                    )}

                    <Button
                      variant="contained"
                      fullWidth
                      disabled={
                        roleCreationName?.length === 0 ||
                        Boolean(roleCreationError)
                      }
                      onClick={handleCreateRoleSubmit}
                      startIcon={
                        isCreatingRole && (
                          <CircularProgress size={20} color="inherit" />
                        )
                      }
                      sx={{
                        backgroundColor: "primary.main",
                        "&:hover": {
                          backgroundColor: "customColors.core"
                        }
                      }}
                    >
                      {isCreatingRole
                        ? t("common.creating")
                        : t("common.create")}
                    </Button>
                  </Box>
                </Modal>

                {/*Delete Role confirmation modal*/}
                <Dialog
                  open={deleteRoleConfirmOpen}
                  onClose={handleCloseRoleDeleteModal}
                  aria-labelledby="confirm-delete-role"
                >
                  <DialogTitle id="confirm-dialog-title">
                    {t("administration.masters.are_you_sure_to_delete_master")}
                  </DialogTitle>
                  <DialogContent>
                    <DialogContentText id="confirm-dialog-description">
                      {t("common.this_action_cannot_be_undone")}
                    </DialogContentText>
                  </DialogContent>
                  <DialogActions>
                    <Button
                      onClick={handleCloseRoleDeleteModal}
                      color="primary"
                      variant="outlined"
                      sx={{
                        textTransform: "capitalize"
                      }}
                    >
                      {t("common.cancel")}
                    </Button>
                    <Button
                      onClick={handleConfirmDeleteRole}
                      color="error"
                      variant="contained"
                      autoFocus
                      sx={{
                        textTransform: "capitalize"
                      }}
                    >
                      {t("common.delete")}
                    </Button>
                  </DialogActions>
                </Dialog>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Box>
    </Box>
  );
};

export default AdministratorPanel;
